import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../services/api';
import { Building2, Edit, Trash2, Plus, ArrowLeft } from 'lucide-react';

export default function TenantList() {
  const queryClient = useQueryClient();
  const [includeInactive, setIncludeInactive] = useState(false);

  const { data: tenantsData, isLoading } = useQuery({
    queryKey: ['tenants', includeInactive],
    queryFn: async () => {
      const response = await adminApi.getTenants(includeInactive);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminApi.deleteTenant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const handleDelete = async (id: number, companyName: string) => {
    if (confirm(`Are you sure you want to deactivate "${companyName}"?`)) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (err: any) {
        alert(err.response?.data?.message || 'Failed to delete tenant');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tenants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/dashboard"
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Tenant Management</h1>
                <p className="text-sm text-gray-600">
                  {tenantsData?.count || 0} total tenants
                </p>
              </div>
            </div>
            <Link
              to="/admin/tenants/new"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Tenant
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="card mb-6 p-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show inactive tenants</span>
          </label>
        </div>

        {/* Tenants Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tenantsData?.tenants?.map((tenant: any) => (
            <div
              key={tenant.id}
              className="card card-hover"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {tenant.logo_url ? (
                      <img
                        src={tenant.logo_url}
                        alt={tenant.company_name}
                        className="w-12 h-12 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{tenant.company_name}</h3>
                      <p className="text-sm text-gray-500">{tenant.subdomain}</p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      tenant.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {tenant.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div>
                    <p className="text-xs text-gray-500">OData URL</p>
                    <p className="text-sm text-gray-900 truncate" title={tenant.odata_base_url}>
                      {tenant.odata_base_url}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Company ID</p>
                    <p className="text-sm text-gray-900 font-mono">{tenant.bc_company_id}</p>
                  </div>
                  {tenant.primary_color && (
                    <div>
                      <p className="text-xs text-gray-500">Primary color</p>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: tenant.primary_color }}
                        ></div>
                        <p className="text-sm text-gray-900 font-mono">{tenant.primary_color}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <Link
                    to={`/admin/tenants/${tenant.id}/edit`}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 font-medium text-sm rounded-xl hover:bg-blue-100 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(tenant.id, tenant.company_name)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-700 font-medium text-sm rounded-xl hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {tenantsData?.tenants?.length === 0 && (
          <div className="card p-12 text-center">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="mb-2 text-base font-semibold text-ink">No tenants found</h3>
            <p className="text-gray-600 mb-6">
              {includeInactive
                ? 'No tenants exist yet.'
                : 'No active tenants found. Try showing inactive tenants.'}
            </p>
            <Link
              to="/admin/tenants/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create First Tenant
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
